import ifConventionAxios from "../../../api/ifConventionAxios";

// -----------------------------------------------------------------------------
// -----------------------------------------------------------------------------
// Session/Local Auth Helpers
// -----------------------------------------------------------------------------

// Returns the currently logged-in participant's auth/session info from sessionStorage
export function getLoggedInParticipant() {
  try {
    const data = sessionStorage.getItem('participantAuth');
    if (!data) return null;
    return JSON.parse(data);
  } catch (e) {
    return null;
  }
}

// Stub for updateParticipantInfo if not present
export function updateParticipantInfo() {
  // Implement as needed
}
// -----------------------------------------------------------------------------
// Session/Local Auth Helpers
// -----------------------------------------------------------------------------

// Log out the current participant (clears sessionStorage)
export function logoutParticipant() {
  try {
    sessionStorage.removeItem('participantAuth');
    // Optionally clear other session keys if needed
  } catch (e) {
    // Ignore errors
  }
}
// Config / Constants
// -----------------------------------------------------------------------------

const ACCOUNT_ID = 7;
const bearerAuthHeader = `Bearer ${ACCOUNT_ID}`;

// Using dedicated IF Convention axios instance - baseURL already configured

// Default bearer config (used by most calls)
const config = {
  headers: { Authorization: bearerAuthHeader },
};

const buildTokenConfig = (authToken, extraHeaders = {}) => ({
  headers: {
    Authorization: `Token ${authToken.Token}_${ACCOUNT_ID}`,
    ...extraHeaders,
  },
});

const getErrorMessage = (error, defaultMessage) =>
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  defaultMessage;

// -----------------------------------------------------------------------------
// Convention: SignOn / PIN (new flow)
// -----------------------------------------------------------------------------

// Request PIN code (SignOn)
export async function requestPin(
  participantId,
  method,
  deviceId,
  accountId = `${ACCOUNT_ID}`
) {
  const endpoint = `Convention/SignOn`;
  const requestData = { participantId, method, deviceId };

  try {
    const response = await ifConventionAxios.post(endpoint, requestData, {
      headers: { Authorization: `Bearer ${accountId}` },
    });

    return {
      success: response.data.success,
      verificationId: response.data.verificationId,
      expiresIn: response.data.expiresIn,
      message: response.data.message,
    };
  } catch (error) {

    return {
      success: false,
      message: getErrorMessage(error, "Failed to send PIN code"),
    };
  }
}

// Verify PIN code
export async function verifyPin(
  verificationId,
  pin,
  deviceId,
  accountId = `${ACCOUNT_ID}`
) {
  const endpoint = `Convention/VerifyPin`;
  const requestData = { verificationId, pin, deviceId };

  try {
    const response = await ifConventionAxios.post(endpoint, requestData, {
      headers: { Authorization: `Bearer ${accountId}` },
    });

    const participant = response.data.participantId
      ? {
          id: response.data.participantId,
          fullName: response.data.participantName,
          email: response.data.email,
          mobile: response.data.mobile,
        }
      : null;

    return {
      success: response.data.success,
      token: response.data.token,
      expiresIn: response.data.expiresIn,
      participant,
      message: response.data.message,
    };
  } catch (error) {

    return {
      success: false,
      message: getErrorMessage(error, "Invalid PIN code"),
    };
  }
}

// -----------------------------------------------------------------------------
// Convention: Handler requests
// -----------------------------------------------------------------------------

// Send handler request
export async function sendHandlerRequest(breederId, handlerId, requestData) {
  const endpoint = `Convention/SendHandlerRequest`;
  const body = { breederId, handlerId, ...requestData };

  try {
    const response = await ifConventionAxios.post(endpoint, body, config);
    return response.data;
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, "Failed to send handler request."),
    };
  }
}

// Get handler requests
export async function getHandlerRequests(handlerId) {
  const endpoint = `Convention/GetHandlerRequestsByParticipantId`;

  try {
    const response = await ifConventionAxios.get(endpoint, {
      params: { participantId: handlerId },
      ...config,
    });
    return response.data;
  } catch (error) {
    return {
      success: false,
      requests: [],
      message: getErrorMessage(error, "Failed to load handler requests."),
    };
  }
}

// Get breeder requests (stub)
export async function getBreederRequests(breederId) {
  return { success: false, requests: [], message: "API not implemented" };
}

// Approve handler request (stub)
export async function approveHandlerRequest(requestId, handlerId) {
  return { success: false, message: "API not implemented" };
}

// Reject handler request (stub)
export async function rejectHandlerRequest(requestId, handlerId, reason) {
  return { success: false, message: "API not implemented" };
}

// Get admin rotation requests 
export async function getAdminRotationRequests() {
  try {
    
    // Since admin-rotation and admin-rotation-mandatory are virtual handler IDs,
    // we need to get ALL breeder requests and filter for admin rotation requests
    const endpoint = `Convention/GetAllHandlerRequests`; // Need this endpoint
    
    try {
      const response = await ifConventionAxios.get(endpoint, config);
      
      if (response.data && response.data.success && response.data.requests) {
        // Filter for admin rotation requests
        const adminRotationRequests = response.data.requests.filter(request => 
          request.handlerId === 'admin-rotation' || 
          request.handlerId === 'admin-rotation-mandatory' ||
          request.isAdminRotation === true
        );
        

        
        return {
          success: true,
          requests: adminRotationRequests,
          message: `Loaded ${adminRotationRequests.length} admin rotation requests`
        };
      } else {
        return {
          success: false,
          requests: [],
          message: response.data?.message || "No requests returned from API"
        };
      }
    } catch (apiError) {
      // Fallback: Try to get requests by scanning all participants
      // This is less efficient but should work if GetAllHandlerRequests doesn't exist
      return {
        success: false,
        requests: [],
        message: "GetAllHandlerRequests endpoint not available. Backend needs to implement this endpoint to fetch admin rotation requests."
      };
    }
  } catch (error) {

    return {
      success: false,
      requests: [],
      message: getErrorMessage(error, "Failed to load admin rotation requests.")
    };
  }
}

// Assign handler to admin rotation (stub)
export async function assignHandlerToRotation(
  requestId,
  handlerId,
  birdsToAssign
) {
  return { success: false, message: "API not implemented" };
}

// Cancel a handler request
export async function cancelHandlerRequest(participantId, requestId) {
  const endpoint = `Convention/CancelHandlerRequest/${participantId}/${requestId}`;

  try {
    const response = await ifConventionAxios.post(endpoint, {}, config);

    if (response.status === 200) {
      return { success: true };
    }

    return {
      success: false,
      message: response.statusText || "Failed to cancel handler request.",
    };
  } catch (error) {
    return {
      success: false,
      message: getErrorMessage(error, "Failed to cancel handler request."),
    };
  }
}

// -----------------------------------------------------------------------------
// Convention: Participants
// -----------------------------------------------------------------------------

// Get all participants
export async function GetParticipants() {
  const endpoint = `Convention/GetParticipants`;

  try {
    const response = await ifConventionAxios.get(endpoint, {
      headers: { Authorization: bearerAuthHeader },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Get a single participant by ID
export async function GetParticipantById(participantId) {
  const endpoint = `Participants/GetParticipant`;

  try {
    const response = await ifConventionAxios.get(endpoint, {
      headers: { Authorization: bearerAuthHeader },
      params: { participantId },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Save or update participant information
export async function SaveParticipant(participant, authToken) {
  const participantId = participant.id;
  if (!participantId) {
    throw new Error("Participant id is required");
  }

  const endpoint = `Convention/UpdateParticipant/${participantId}`;
  const requestConfig = buildTokenConfig(authToken, {
    "Content-Type": "application/json",
  });

  try {
    const response = await ifConventionAxios.post(endpoint, participant, requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Delete a participant
export async function DeleteParticipant(participantId, authToken) {
  const endpoint = `Convention/DeleteParticipant/${participantId}`;
  const requestConfig = buildTokenConfig(authToken);

  try {
    const response = await ifConventionAxios.delete(endpoint, requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// -----------------------------------------------------------------------------
// Auth: PIN / passcode / device auth
// -----------------------------------------------------------------------------

// Send PIN code to participant
export async function SendPincode(email, method, deviceId) {
  const endpoint = `Auth/SendPincode`;
  const data = { email, method, deviceId };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Verify PIN code
export async function VerifyPincode(email, pincode, deviceId) {
  const endpoint = `Auth/VerifyPincode`;
  const data = { email, pincode, deviceId };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Verify passcode (for email verification)
export async function VerifyPasscode(email, passcode, emailOnly = false) {
  const endpoint = `Auth/VerifyPasscode`;
  const data = { email, passcode, emailOnly };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Lower-case alias for import compatibility
export const verifyPasscode = VerifyPasscode;

// Save device authentication
export async function SaveDeviceAuth(email, deviceId, passcode) {
  const endpoint = `Auth/SaveDeviceAuth`;
  const data = { email, deviceId, passcode };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Verify device authentication
export async function VerifyDeviceAuth(email, deviceId, passcode) {
  const endpoint = `Auth/VerifyDeviceAuth`;
  const data = { email, deviceId, passcode };

  try {
    const response = await ifConventionAxios.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// -----------------------------------------------------------------------------
// Requests (race requests)
// -----------------------------------------------------------------------------

// Get all requests for a race
export async function GetRequests(raceId, year) {
  const endpoint = `Requests/GetRequests`;

  try {
    const response = await ifConventionAxios.get(endpoint, {
      headers: { Authorization: bearerAuthHeader },
      params: { raceId, year },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Save a new request
export async function SaveRequest(request, authToken) {
  request.AccountID = ACCOUNT_ID;

  const endpoint = `Requests/SaveRequest`;
  const requestConfig = buildTokenConfig(authToken);

  try {
    const response = await ifConventionAxios.post(endpoint, request, requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
}

// Update request status
export async function UpdateRequestStatus(requestId, status, authToken) {
  const endpoint = `Requests/UpdateStatus`;
  const requestConfig = buildTokenConfig(authToken);
  const data = { requestId, status };

  try {
    const response = await ifConventionAxios.post(endpoint, data, requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
}
